import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { BLOCK_GROUPS } from './sectionLayouts';
import { blockIcons, blockLabels } from '../content-editor/blockTypes';

/**
 * Squarespace-style "Add a Block" picker.
 *
 * Iter 190 — LEFT-SIDE POPOVER (was: centered modal with dark backdrop).
 *   Previously the picker rendered as a centered modal with a full-viewport
 *   `bg-black/60` backdrop. That backdrop darkened the entire canvas the
 *   instant the picker opened, which read as a "content flash" in the user's
 *   screen recording — even though no DOM mutation was actually happening on
 *   the section. It also blocked the vendor from seeing where their new block
 *   would appear.
 *
 *   The new design mirrors Squarespace / Wix: the panel is anchored to the
 *   LEFT edge of the viewport (immediately right of the app sidebar), with NO
 *   backdrop. The canvas stays fully visible and un-dimmed. Clicking anywhere
 *   outside the panel closes it (handled by a mousedown listener on document).
 */
export default function AddBlockMenu({ open, onClose, onPick }) {
  const [query, setQuery] = useState('');
  const panelRef = useRef(null);
  // Iter 175k — Memoize the filtered groups so they don't rebuild on
  // every parent re-render.
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return BLOCK_GROUPS
      .map(g => ({ ...g, items: g.items.filter(i => !q || (i.label || '').toLowerCase().includes(q)) }))
      .filter(g => g.items.length > 0);
  }, [query]);

  // Close on outside click (no backdrop element — canvas stays interactable-looking)
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay listener attachment by one tick so the click that OPENED the menu
    // doesn't immediately close it. Use CAPTURE phase so events aren't
    // swallowed by section-level handlers that stopPropagation.
    const t = setTimeout(() => document.addEventListener('mousedown', handleMouseDown, true), 0);
    // Also close on Escape.
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Left-anchored floating panel — sits immediately right of the app sidebar (~240px wide),
          top offset accounts for the builder top bar (~64px). No backdrop, no dimming — the
          canvas remains fully visible so vendors can see where their new block will land. */}
      <div
        ref={panelRef}
        className="fixed left-[248px] top-[176px] bottom-[24px] z-[181] w-[380px] bg-[#15152E] border border-white/[0.10] rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col"
        data-testid="add-block-menu"
      >
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">Add a Block</span>
            <p className="text-white text-[14px] font-bold mt-0.5">What do you want to add?</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-1.5" data-testid="add-block-close"><X size={16} /></button>
        </div>
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search blocks..."
              className="w-full bg-white/[0.04] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
              data-testid="block-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 sqs-scrollbar">
          {filtered.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{group.label}</p>
              <div className="grid grid-cols-3 gap-2">
                {group.items.map((item) => {
                  const Icon = blockIcons[item.type];
                  return (
                    <button
                      key={item.type}
                      onClick={() => { onPick(item.type); onClose(); }}
                      className="flex flex-col items-center gap-2 py-4 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-[#8B5CF6]/15 hover:border-[#8B5CF6]/40 transition-colors text-center group"
                      data-testid={`block-pick-${item.type}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8B5CF6]/20 flex items-center justify-center transition-colors">
                        {Icon && <Icon size={16} className="text-[#9CA3AF] group-hover:text-[#A78BFA] transition-colors" />}
                      </div>
                      <span className="text-[11px] font-medium text-[#9CA3AF] group-hover:text-white transition-colors leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
